import { useMemo } from 'react'
import { paymentGatewaysConfig } from 'src/assets/SettingsConfig'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { ChartSkeleton } from 'src/components/overview/charts/common/Skeleton'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'
import { NoData } from 'src/components/analytics/common/NoData'
import { percentageWithFixedDecmials } from 'src/lib/utils/analytics'

type PaymentGatewaySuccessRateData = {
  conversion_rate: number
  gateway: string
  order_completed: string
  payment_initiated: string
}[]

const paymentGatewayWithImageConfig = paymentGatewaysConfig.reduce((acc, item) => {
  acc[item.type] = {
    imgPath: item.imgPath
  }
  return acc
}, {} as Record<string, { imgPath: string }>)

export default function PaymentGatewaySuccessRate() {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.payment_gateways_success_rate,
      dependencies: {
        endpoints: ['/payment-gateway-conversion']
      }
    },
    refreshKey
  })

  const paymentGatewayData = useMemo(() => {
    const rawData: PaymentGatewaySuccessRateData = data[0]?.data?.result?.widgetData?.data?.data
    const comparedData: PaymentGatewaySuccessRateData = data[0]?.data?.result?.widgetData?.compared_data?.data

    if (!rawData || rawData.length === 0) return { data: [] }
    // Create a map for previous period conversion rates
    const comparedMap = (comparedData || []).reduce((acc, item) => {
      acc[item.gateway] = {
        conversion_rate: item.conversion_rate,
        order_completed: Number(item.order_completed),
        payment_initiated: Number(item.payment_initiated)
      }
      return acc
    }, {} as Record<string, { conversion_rate: number; order_completed: number; payment_initiated: number }>)

    const finalData = rawData.map(item => {
      const prevRate = comparedMap[item.gateway] ?? {
        conversion_rate: 0,
        order_completed: 0,
        payment_initiated: 0
      }
      const trend = {
        conversion_rate_percentage: percentageWithFixedDecmials(Number(item.conversion_rate), prevRate.conversion_rate),
        order_completed_percentage: percentageWithFixedDecmials(Number(item.order_completed), prevRate.order_completed),
        payment_initiated_percentage: percentageWithFixedDecmials(
          Number(item.payment_initiated),
          prevRate.payment_initiated
        )
      }
      return {
        ...item,
        imgPath: paymentGatewayWithImageConfig[item.gateway]?.imgPath,
        trend
      }
    })
    return {
      data: finalData
    }
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (paymentGatewayData.data.length > 0) {
      return (
        <div className='bg-white rounded-xl mt-2'>
          <table className='w-full text-left'>
            <thead className='sticky top-0 bg-white'>
              <tr className='text-xs text-gray-500'>
                <th className='py-2'> </th>
                <th className='py-2'> </th>
                <th className='py-2 text-right'>Total initiations</th>
                <th className='py-2 text-right'>Total completions</th>
                <th className='py-2 text-right'>Conversion rate</th>
              </tr>
            </thead>
            <tbody>
              {paymentGatewayData.data.map((item, idx) => (
                <>
                  <tr key={item.gateway + idx} className='bg-gray-lightest'>
                    <td className='py-2 w-8 pl-2'>
                      {item.imgPath && <img src={item.imgPath} alt={item.gateway} className='w-6 h-6 rounded' />}
                    </td>
                    <td className='py-2 font-bold text-sm pl-2'>{item.gateway}</td>
                    <td className='py-3 font-bold text-sm text-right align-text-bottom'>
                      {item.payment_initiated}
                      <span className='inline-block ml-2'>
                        {item.trend?.payment_initiated_percentage ? (
                          <TrendIndicator
                            variant='small'
                            value={item.trend.payment_initiated_percentage}
                            formatter={value => `${Math.abs(value)}%`}
                          />
                        ) : null}
                      </span>
                    </td>
                    <td className='py-3 font-bold text-sm text-right align-text-bottom'>
                      {item.order_completed}
                      <span className='inline-block ml-2'>
                        {item.trend?.order_completed_percentage ? (
                          <TrendIndicator
                            value={item.trend.order_completed_percentage}
                            formatter={value => `${Math.abs(value)}%`}
                            variant='small'
                          />
                        ) : null}
                      </span>
                    </td>
                    <td className='py-3 font-bold text-sm space-x-1 text-right align-text-bottom'>
                      <span className='text-sm'>{item.conversion_rate}%</span>
                      <span className='inline-block ml-2'>
                        {item.trend?.conversion_rate_percentage ? (
                          <TrendIndicator
                            value={item.trend.conversion_rate_percentage}
                            formatter={value => `${Math.abs(value)}%`}
                            variant='small'
                          />
                        ) : null}
                      </span>
                    </td>
                  </tr>
                  <tr className='bg-white'>
                    <td colSpan={5} className='py-[2px]'></td>
                  </tr>
                </>
              ))}
            </tbody>
          </table>
        </div>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full overflow-y-auto mt-2 pb-4'>
      {renderComponent()}
    </div>
  )
}
